import React, { useCallback, useEffect, useRef, useState } from 'react'
import { OTPInputProps } from './types'

export function OTPInput({
  codes,
  setCodes,
  onSubmit,
  isSubmitting,
  autoFocus,
  autoFocusAfterClean,
  cellStyle,
  variant = 'filled',
  unfocusedBorderColor = '#374151',
  focusedBorderColor = '#3B82F6',
  numberCells = 4,
  errorMessage,
  setErrorMessage,
  keyboardType = 'numeric',
  textContentType = 'one-time-code',
  ...rest
}: OTPInputProps) {
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null)
  const hasSubmittedRef = useRef(false)
  const refs = useRef<(HTMLInputElement | null)[]>(
    Array(numberCells).fill(null),
  )

  const handleFocus = (index: number) => setFocusedIndex(index)
  const handleBlur = () => setFocusedIndex(null)

  // Получаем Tailwind классы для ячейки
  const getCellClasses = useCallback(
    (index: number): string => {
      const isFocused = focusedIndex === index
      const hasError = !!errorMessage
      const hasValue = codes[index] && codes[index].length > 0

      const classes: string[] = [
        'w-20', // width: 80px
        'h-20', // height: 80px
        'text-center', // textAlign: center
        'text-3xl', // fontSize: 32px
        'font-semibold', // fontWeight: 600
        'outline-none', // outline: none
        'transition-all', // transition: all
        'duration-200', // 0.2s
        'ease-in-out', // ease
        'border-2', // border: 2px solid
      ]

      // Вариант стилизации
      if (variant === 'standard') {
        classes.push(
          'bg-transparent', // backgroundColor: transparent
          'border-0', // border: none
          'border-b-2', // borderBottom: 2px solid
          'rounded-none', // borderRadius: 0
          'pb-1.5', // paddingBottom: 6px
        )
      } else {
        classes.push(
          'rounded-2xl', // borderRadius: 16px
        )

        // Background для filled варианта
        if (variant === 'filled') {
          classes.push('bg-gray-800') // backgroundColor: #1F2937
        } else {
          classes.push('bg-transparent')
        }
      }

      // Цвет текста
      if (hasError) {
        classes.push('text-red-500') // color: #EF4444
      } else {
        classes.push('text-white') // color: #FFFFFF
      }

      // Цвет границы
      if (hasError) {
        classes.push('border-red-500')
      } else if (isFocused || hasValue) {
        // Используем динамические цвета для focused состояния
        if (focusedBorderColor === '#3B82F6') {
          classes.push('border-blue-500')
        } else {
          // Для кастомных цветов используем inline стили
        }
      } else {
        // Для unfocused состояния
        if (unfocusedBorderColor === '#374151') {
          classes.push('border-gray-700')
        } else {
          // Для кастомных цветов используем inline стили
        }
      }

      return classes.join(' ')
    },
    [
      focusedIndex,
      errorMessage,
      codes,
      variant,
      focusedBorderColor,
      unfocusedBorderColor,
    ],
  )

  // Получаем inline стили для кастомных цветов
  const getCellStyle = useCallback(
    (index: number): React.CSSProperties => {
      const isFocused = focusedIndex === index
      const hasError = !!errorMessage
      const hasValue = codes[index] && codes[index].length > 0

      const customStyles: React.CSSProperties = {
        ...cellStyle,
      }

      // Применяем кастомные цвета границ если они не стандартные
      if (!hasError) {
        if (isFocused || hasValue) {
          if (focusedBorderColor !== '#3B82F6') {
            if (variant === 'standard') {
              customStyles.borderBottomColor = focusedBorderColor
            } else {
              customStyles.borderColor = focusedBorderColor
            }
          }
        } else {
          if (unfocusedBorderColor !== '#374151') {
            if (variant === 'standard') {
              customStyles.borderBottomColor = unfocusedBorderColor
            } else {
              customStyles.borderColor = unfocusedBorderColor
            }
          }
        }
      }

      return customStyles
    },
    [
      focusedIndex,
      errorMessage,
      codes,
      variant,
      focusedBorderColor,
      unfocusedBorderColor,
      cellStyle,
    ],
  )

  const handleChangeText = (text: string, index: number) => {
    if (isSubmitting) return

    // Clear error when user starts typing
    if (errorMessage && setErrorMessage) {
      setErrorMessage('')
    }

    const newCodes = [...codes]

    // Handle paste of full code
    if (text.length > 1) {
      const pastedCode = text.slice(0, numberCells)
      for (let i = 0; i < numberCells; i++) {
        newCodes[i] = pastedCode[i] || ''
      }
      setCodes(newCodes)

      // Focus last filled cell
      const lastFilledIndex = Math.min(pastedCode.length - 1, numberCells - 1)
      if (refs.current[lastFilledIndex]) {
        refs.current[lastFilledIndex]?.focus()
      }
      return
    }

    // Handle single character input
    if (text.match(/^\d?$/)) {
      newCodes[index] = text
      setCodes(newCodes)

      // Auto-focus next field
      if (text && index < numberCells - 1) {
        refs.current[index + 1]?.focus()
      }
    }
  }

  const handleKeyPress = (key: string, index: number) => {
    if (key === 'Backspace' && !codes[index] && index > 0) {
      // Focus previous field on backspace if current is empty
      refs.current[index - 1]?.focus()
    }
  }

  // Auto-submit when all fields are filled
  useEffect(() => {
    const fullCode = codes.join('')

    if (fullCode.length === numberCells && !hasSubmittedRef.current) {
      hasSubmittedRef.current = true
      onSubmit(fullCode)
    } else if (fullCode.length < numberCells) {
      hasSubmittedRef.current = false
    }
  }, [codes, numberCells, onSubmit])

  // Auto-focus after clean
  useEffect(() => {
    if (autoFocusAfterClean && codes.every((code) => !code)) {
      refs.current[0]?.focus()
    }
  }, [codes, autoFocusAfterClean])

  return (
    <div className="flex flex-col gap-2">
      <div className="flex justify-between gap-2">
        {codes.map((code, index) => (
          <input
            key={index}
            ref={(el) => (refs.current[index] = el)}
            type="text"
            inputMode="numeric"
            autoComplete={textContentType}
            maxLength={index === 0 ? numberCells : 1}
            value={code}
            autoFocus={autoFocus && index === 0}
            className={`${getCellClasses(index)} ym-hide-content`}
            style={getCellStyle(index)}
            onFocus={() => handleFocus(index)}
            onBlur={handleBlur}
            onChange={(e) => handleChangeText(e.target.value, index)}
            onKeyDown={(e) => handleKeyPress(e.key, index)}
            disabled={isSubmitting}
            {...rest}
          />
        ))}
      </div>
      {errorMessage && (
        <div className="text-red-500 text-sm text-center mt-2">
          {errorMessage}
        </div>
      )}
    </div>
  )
}
